import { useQuery } from '@apollo/client'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Button, Chip } from '@mui/material'
import { ArrowBack, Domain, Person } from '@mui/icons-material'

import { useAuth } from '../../hooks/useAuth'
import { useBranding } from '../../hooks'
import { queries } from '../../graphql'
import { detectActiveContext, resolvePortalPhraseParts } from '../context-switcher/compute-contexts'

export function ImpersonationBanner() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [{ isImpersonating, impersonationType }, { switchBack }] = useAuth()
  const branding = useBranding()

  const { data: meData } = useQuery(queries.user.me, {
    skip: !isImpersonating,
  })

  if (!isImpersonating) {
    return null
  }

  const me = meData?.me
  const userName = me?.name || me?.email || 'Client'
  const parts = resolvePortalPhraseParts(detectActiveContext(pathname), me)
  const nameLine = `Managing on behalf of: ${parts?.name || userName}`

  const isAdminImpersonation = impersonationType === 'ADMIN'
  const returnButtonLabel = isAdminImpersonation ? 'Return to Admin' : 'Return to Advisor Dashboard'

  const handleSwitchBack = async () => {
    const returnPath = impersonationType === 'ADMIN' ? '/management' : '/funds'
    // Navigate BEFORE switching back to avoid the current portal's guard
    // reacting to the token change and flashing a 404
    navigate(returnPath, { replace: true })
    await switchBack()
  }

  const accentColor = branding?.accentColor || '#eccb48'
  const accentTextColor = branding?.accentTextColor || '#1a2e52'

  return (
    <Box
      sx={{
        backgroundColor: accentColor,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 0.75,
      }}
    >
      <Chip
        label={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3, py: 0.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person fontSize="inherit" />
              <Box component="span">{nameLine}</Box>
            </Box>
            {parts?.typeLine && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.85 }}>
                <Domain fontSize="inherit" />
                <Box component="span">{parts.typeLine}</Box>
              </Box>
            )}
          </Box>
        }
        size="small"
        sx={{
          height: 'auto',
          backgroundColor: 'rgba(255,255,255,0.25)',
          color: accentTextColor,
          '& .MuiChip-label': { whiteSpace: 'normal' },
        }}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={<ArrowBack />}
        onClick={handleSwitchBack}
        sx={{
          position: 'absolute',
          right: 16,
          whiteSpace: 'nowrap',
          color: accentTextColor,
          borderColor: accentTextColor,
          '&:hover': {
            borderColor: accentTextColor,
            backgroundColor: 'rgba(255,255,255,0.15)',
          },
        }}
      >
        {returnButtonLabel}
      </Button>
    </Box>
  )
}
